import React from 'react';

import BackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AppBar from '@mui/material/AppBar';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MuiToolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTheme } from '@mui/material';
import { Link, useNavigate, getRouteApi } from '@tanstack/react-router';
import ContentView from '~/components/ContentView';
import UserForm from '~/components/UserForm';
import { usePublisher } from '~/hooks/api/usePublisher';
import { toE164 } from '~/utils/phone-number-helpers';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import DeleteButton from './DeleteButton';
import messages from './messages';
import UserStatus from './UserStatus';
import { useEditPublisher } from '~/hooks/api/useEditPublisher';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import { CountryCode } from 'libphonenumber-js';

const route = getRouteApi('/users/$publisherId/');

export const UserEdit = () => {
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const search = route.useSearch();
  const { publisherId } = route.useParams();
  const publisherIdNum = parseInt(publisherId, 10);
  const { data: settings } = useSettingsByCategory('masterdata');
  const { data: publisherData, isFetching } = usePublisher(publisherIdNum, [
    'partner',
  ]);
  const handleBackButton = () => {
    const query = new URLSearchParams(search as string);
    navigate({ to: query.get('from') || '/users' });
  };

  const editPublisherMutation = useEditPublisher(publisherIdNum, {
    onSuccess() {
      showSnackbar({
        message: intl.formatMessage(messages.saveSuccess),
      });
      handleBackButton();
    },
    onError() {
      showSnackbar({
        message: intl.formatMessage(messages.saveFailure),
      });
    },
  });

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<Element | null>(null);

  return (
    <ContentView
      header={
        <AppBar position="static">
          <MuiToolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="back"
              onClick={handleBackButton}
              style={{ marginRight: theme.spacing(2) }}
              size="large"
            >
              <BackIcon />
            </IconButton>
            <Typography variant="h6" style={{ flex: 1 }}>
              <FormattedMessage {...messages.title} />
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="display more actions"
              onClick={(event) => setMenuAnchorEl(event.target as Element)}
              size="large"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={menuAnchorEl}
              keepMounted
              open={!!menuAnchorEl}
              onClose={() => setMenuAnchorEl(null)}
            >
              {!isFetching && publisherData ? (
                <DeleteButton
                  publisherId={publisherData.id}
                  onDeleteSuccess={handleBackButton}
                />
              ) : null}
              <MenuItem
                component={Link}
                to={`/users/${publisherId}/audit?from=${encodeURIComponent(
                  `${location.pathname}${location.search}`
                )}`}
              >
                <FormattedMessage {...messages.auditLog} />
              </MenuItem>
              <MenuItem
                component={Link}
                to={`/availability?publisherId=${publisherId}&from=${encodeURIComponent(
                  `${location.pathname}${location.search}`
                )}`}
              >
                <FormattedMessage {...messages.absences} />
              </MenuItem>
              <MenuItem
                component={Link}
                to={`/user-profile?publisherId=${publisherId}&from=${encodeURIComponent(
                  `${location.pathname}${location.search}`
                )}`}
              >
                <FormattedMessage {...messages.editProfile} />
              </MenuItem>
            </Menu>
          </MuiToolbar>
        </AppBar>
      }
      isLoading={isFetching}
    >
      {!isFetching && publisherData ? (
        <UserStatus
          state={publisherData.state || undefined}
          publisher={publisherData}
        />
      ) : null}
      <Divider />
      {!isFetching && publisherData && settings ? (
        <UserForm
          onSubmit={(values) => {
            editPublisherMutation.mutate({
              ...values,
              middleName: values.middleName || undefined,
              phone: toE164(
                values.phone,
                settings.defaultRegionCallingCode as CountryCode
              ),
              birthDate: values.birthDate || null,
            });
          }}
          initialValues={{
            ...publisherData,
            congregation: publisherData.congregation.toString(),
            birthDate: publisherData.birthDate || null,
          }}
          emailFixed={Boolean(publisherData.externalId)}
        />
      ) : null}
    </ContentView>
  );
};
export default UserEdit;
